import { useState, ReactNode } from 'react'
import { Tabs, TabsProps } from 'antd'
import { PageHeader } from '@/components/patterns'
import type { PageHeaderProps } from '@/components/patterns'

type TabItem = {
  key: string
  label: ReactNode
  children: ReactNode
  icon?: ReactNode
}

interface TabbedPageProps extends Omit<PageHeaderProps, 'children'> {
  /**
   * Array of tab items to display
   */
  tabs: TabItem[]

  /**
   * Default active tab key
   */
  defaultActiveKey?: string

  /**
   * Controlled active tab key
   */
  activeKey?: string

  /**
   * Callback when tab changes
   */
  onTabChange?: (key: string) => void

  /**
   * Additional className for the tabs container
   */
  tabClassName?: string

  /**
   * Additional className for the content section
   */
  containerClassName?: string

  /**
   * Whether to wrap tabs in a white card container
   * @default true
   */
  showTabContainer?: boolean

  /**
   * Size of the tabs
   * @default 'large'
   */
  tabSize?: 'small' | 'middle' | 'large'

  /**
   * Custom tab bar style
   */
  tabBarStyle?: React.CSSProperties

  /**
   * Additional Ant Design Tabs props
   */
  tabsProps?: Omit<TabsProps, 'items' | 'activeKey' | 'onChange' | 'size' | 'className'>

  /**
   * Optional content to render between header and tabs
   */
  children?: ReactNode
}

/**
 * TabbedPage Component
 *
 * Standardized layout for tab-based pages. Combines PageHeader with Ant Design Tabs
 * in a consistent wrapper structure. Supports optional content between header and tabs.
 *
 * @example
 * // Basic usage
 * <EventLayout>
 *   <TabbedPage
 *     title="Email Blast"
 *     subtitle="Manage your email campaigns"
 *     tabs={[
 *       { key: "manual", label: "Manual Email", children: <ManualEmailList /> },
 *       { key: "automated", label: "Automated Email", children: <AutomatedEmailList /> }
 *     ]}
 *   />
 * </EventLayout>
 *
 * @example
 * // With icons and header actions
 * <TabbedPage
 *   title="Lucky Draw"
 *   subtitle="Configure eligibility rules"
 *   headerAction={<Button>Settings</Button>}
 *   tabs={[
 *     {
 *       key: "settings",
 *       label: <TabIconLabel icon={<HiOutlineCog />} label="Settings" />,
 *       children: <SettingsTab />
 *     }
 *   ]}
 * />
 *
 * @example
 * // With content between header and tabs
 * <TabbedPage
 *   title="Registration Form"
 *   subtitle="Configure registration fields"
 *   tabs={tabItems}
 * >
 *   <div className="px-6 py-6">
 *     <FormSettingsCard />
 *   </div>
 * </TabbedPage>
 *
 * @example
 * // Without card container (flat tabs)
 * <TabbedPage
 *   title="Agenda Builder"
 *   tabs={tabItems}
 *   showTabContainer={false}
 * />
 */
export const TabbedPage = ({
  title,
  subtitle,
  description,
  headerAction,
  actionButton,
  breadcrumb,
  showBackButton,
  onBack,
  tabs,
  defaultActiveKey,
  activeKey: controlledActiveKey,
  onTabChange,
  tabClassName = '',
  containerClassName = '',
  tabSize = 'small',
  tabBarStyle,
  tabsProps = {},
  children,
  ...headerProps
}: TabbedPageProps) => {
  // Internal state for uncontrolled mode
  const [internalActiveKey, setInternalActiveKey] = useState(
    defaultActiveKey || tabs[0]?.key || ''
  )

  // Use controlled or uncontrolled state
  const activeKey = controlledActiveKey !== undefined ? controlledActiveKey : internalActiveKey

  const handleTabChange = (key: string) => {
    if (controlledActiveKey === undefined) {
      setInternalActiveKey(key)
    }
    onTabChange?.(key)
  }

  return (
    <>
      {/* Page Header */}
      <PageHeader
        title={title}
        subtitle={subtitle}
        description={description}
        headerAction={headerAction}
        actionButton={actionButton}
        breadcrumb={breadcrumb}
        showBackButton={showBackButton}
        onBack={onBack}
        {...headerProps}
      />

      {/* Optional content between header and tabs */}
      {children}

      {/* Content Section */}
      <section className={`px-6 py-4 ${containerClassName}`}>
        <Tabs
          activeKey={activeKey}
          onChange={handleTabChange}
          items={tabs}
          size={tabSize}
          className={tabClassName}
          tabBarStyle={tabBarStyle}
          {...tabsProps}
        />
      </section>
    </>
  )
}

export default TabbedPage
